import { useEffect, useRef, useState } from 'react'

import { fetchTranslation } from '@/api/fetchTranslation'
import { toast } from '@/components/toasts/toast.helpers'
import {
  REALTIME_TRANSCRIPT,
  POSTPROCESSED_TRANSCRIPT,
  PRETTY_TRANSCRIPT,
} from '../transcript.constants'

const TRANSCRIPT_TYPE_TO_TRANSLATION_API = {
  [REALTIME_TRANSCRIPT]: 'transcript',
  [POSTPROCESSED_TRANSCRIPT]: 'postcall_transcript',
  [PRETTY_TRANSCRIPT]: 'pretty_transcript',
}

export const useTranslation = (transcriptType, transcriptToDisplay) => {
  const [translationState, setTranslationState] = useState('idle')
  const [selectedLanguage, setSelectedLanguage] = useState('original')
  const [translatedLanguages, setTranslatedLanguages] = useState([])
  const [translationCache, setTranslationCache] = useState({})
  const abortControllerRef = useRef(null)

  const displayCacheKey = `${TRANSCRIPT_TYPE_TO_TRANSLATION_API[transcriptType]}-${selectedLanguage}`
  const displayTranscript =
    selectedLanguage !== 'original' && translationCache[displayCacheKey]
      ? translationCache[displayCacheKey]
      : transcriptToDisplay

  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    setTranslationState('idle')
    setSelectedLanguage('original')
    setTranslatedLanguages([])

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [transcriptType])

  const handleLanguageChange = async (languageCode) => {
    const transcriptApiType = TRANSCRIPT_TYPE_TO_TRANSLATION_API[transcriptType]
    const cacheKey = `${transcriptApiType}-${languageCode}`

    if (translationCache[cacheKey]) {
      setSelectedLanguage(languageCode)
      setTranslationState('translated')
      return
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    setTranslationState('translating')
    setSelectedLanguage(languageCode)

    let succeeded = false
    try {
      const translated = await fetchTranslation({
        transcript: transcriptToDisplay,
        targetLanguage: languageCode,
        transcriptType: transcriptApiType,
        signal: abortController.signal,
      })

      setTranslationCache((prev) => ({ ...prev, [cacheKey]: translated }))
      succeeded = true
      if (!translatedLanguages.includes(languageCode)) {
        setTranslatedLanguages([...translatedLanguages, languageCode])
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return
      }
      toast.error(error.message || 'Failed to translate transcript')
    } finally {
      if (!abortController.signal.aborted) {
        setTranslationState(succeeded ? 'translated' : 'idle')
        if (!succeeded) {
          setSelectedLanguage('original')
        }
      }
    }
  }

  const handleShowOriginal = () => {
    setSelectedLanguage('original')
    setTranslationState('idle')
  }

  return {
    translationState,
    selectedLanguage,
    translatedLanguages,
    displayTranscript,
    handleLanguageChange,
    handleShowOriginal,
  }
}
